/*
 * Moving between sections: a hypha that runs across the top of the page and
 * is gone.
 *
 * <SectionTransition pathname={location.pathname}>
 *   {children}
 * </SectionTransition>
 *
 * Only a change of SECTION (the first path segment) counts; a query, hash,
 * state or deeper path change within a section does nothing. On a change, a
 * 12-px strip of hypha grows across the top (inside the page's 16-px top
 * padding), its branches appearing just after the tip passes, then fades; it
 * is removed after 700 ms. Mirrored in RTL.
 *
 * The page itself is never animated. A fade of the whole content made every
 * long table its own compositing layer for the fade's length, at the very
 * moment the new page was mounting -- decoration paid for in the first frame
 * of every navigation. For the same reason nothing here runs before that
 * first frame: the width is read and the strip mounted in a passive effect,
 * after the new page has painted, never in a layout effect (which forced a
 * synchronous layout and a second render ahead of the paint).
 *
 * Children are never keyed or remounted, so page state survives navigation.
 * Reduced motion or a hidden tab: nothing at all.
 */
import { useEffect, useRef, useState } from 'react';
import './organic.css';
import { usePrefersReducedMotion, useHtmlDir } from './motion.js';
import { sectionKey, sectionMotif, SWEEP_PATTERN } from './sectionMotif.js';
import { sweepPattern } from './miniBranch.js';
import { hashString } from './prng.js';
import { growStyle } from './artUtil.js';

const SWEEP_MS = 420;

function Sweep({ motif, W, pathname, rtl }) {
  const p = sweepPattern(SWEEP_PATTERN[motif] || 'branches', W, hashString(pathname));
  const after = (at, extra) => Math.round(0.85 * at * SWEEP_MS + extra);
  return (
    <svg className="og-sweep og-decor og-anim" aria-hidden="true" focusable="false" width="100%" height="12"
      viewBox={`0 0 ${W} 12`} preserveAspectRatio="none"
      style={{
        position: 'absolute', top: -14, insetInlineStart: 0, insetInlineEnd: 0, pointerEvents: 'none',
        zIndex: 1, overflow: 'visible', transform: rtl ? 'scaleX(-1)' : undefined,
        animation: 'og-out 200ms var(--og-ease-in) 460ms both',
      }}>
      <path d={p.main.d} className="og-line og-s-strong" strokeWidth="1.5" opacity=".7"
        style={growStyle(p.main.len, SWEEP_MS, 0, 'var(--og-ease-sweep)')} />
      {p.branches.map((b, i) => (
        <path key={i} d={b.d} className="og-line og-s-hypha" strokeWidth="1"
          style={growStyle(b.len, 160, after(b.at, 60), 'var(--og-ease-out)')} />
      ))}
      {p.ticks.map((t, i) => (
        <path key={`t${i}`} d={t.d} className="og-line og-s-strong" strokeWidth="1"
          style={{ animation: `og-fade 120ms var(--og-ease-out) ${after(t.at, 40)}ms both` }} />
      ))}
      {p.spore && <ellipse cx={p.spore.cx} cy={p.spore.cy} rx="2.5" ry="1.8" className="og-f-caplight" />}
      {p.pin && (
        <g className="og-t og-base" style={{ animation: 'og-pop 160ms var(--og-ease-pop) 400ms both' }}>
          <path d={`M${p.pin.x} 6V2`} className="og-line og-s-strong" strokeWidth="1" />
          <path d={`M${p.pin.x - 3} 2.5Q${p.pin.x} -1 ${p.pin.x + 3} 2.5`} className="og-line og-s-strong" strokeWidth="1.2" />
        </g>
      )}
      <g style={{ '--og-w': `${W}px`, animation: `og-tip ${SWEEP_MS}ms var(--og-ease-sweep) both` }}>
        <circle cx="0" cy="6" r="4" className="og-f-tip" opacity=".3" />
        <circle cx="0" cy="6" r="2" className="og-f-tip" />
      </g>
    </svg>
  );
}

export default function SectionTransition({ pathname, children }) {
  const ref = useRef(null);
  const keyRef = useRef(null);
  const countRef = useRef(0);
  const reducedRef = useRef(false);
  const [sweep, setSweep] = useState(null);
  const reduced = usePrefersReducedMotion();
  const dir = useHtmlDir();
  reducedRef.current = reduced;

  useEffect(() => {
    const key = sectionKey(pathname);
    if (keyRef.current === null) {
      keyRef.current = key;
      return;
    }
    if (key === keyRef.current) return;
    keyRef.current = key;
    if (reducedRef.current || (typeof document !== 'undefined' && document.hidden)) return;
    const el = ref.current;
    if (!el) return;
    // After the paint, so the layout this reads is the one just painted.
    const W = el.clientWidth;
    if (W > 0) {
      countRef.current += 1;
      setSweep({ id: countRef.current, motif: sectionMotif(pathname), W, pathname });
    }
  }, [pathname]);

  useEffect(() => {
    if (!sweep) return undefined;
    const id = setTimeout(() => setSweep(null), 700);
    return () => clearTimeout(id);
  }, [sweep]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {sweep && <Sweep key={sweep.id} motif={sweep.motif} W={sweep.W} pathname={sweep.pathname} rtl={dir === 'rtl'} />}
      {children}
    </div>
  );
}
